import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { CreditCard, Bitcoin, Copy, Check, ArrowRight, ShieldCheck, Clock, ExternalLink, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LoyaltyRecap } from "@/components/LoyaltyRecap";

const CRYPTO_TABS = [
  { key: "btc", label: "Bitcoin", ticker: "BTC", chain: "Bitcoin mainnet", explorer: "https://mempool.space/tx/" },
  { key: "usdt_sol", label: "USDT", ticker: "USDT", chain: "Solana (SPL)", explorer: "https://solscan.io/tx/" },
  { key: "usdc_sol", label: "USDC", ticker: "USDC", chain: "Solana (SPL)", explorer: "https://solscan.io/tx/" },
];

export default function Payment() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cardParam = searchParams.get("card"); // success | cancelled | null
  const [order, setOrder] = useState(null);
  const [tab, setTab] = useState("btc");
  const [loading, setLoading] = useState(true);
  const [cardEnabled, setCardEnabled] = useState(false);

  useEffect(() => {
    api.get("/payments/card/config").then((r) => setCardEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cardParam) setTab("card");
    if (cardParam === "cancelled") toast.error("Card payment cancelled — you can retry or pay in crypto.");
  }, [cardParam]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/by-number/${orderNumber}`);
        setOrder(data);
        const pm = (data.payment_method || "").replace("crypto_", "");
        if (CRYPTO_TABS.some((c) => c.key === pm)) setTab(pm);
        if (pm === "card") setTab("card");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Order not found");
        navigate("/account/orders");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderNumber, navigate]);

  if (loading) {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-20 text-center">
        <div className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-muted)]">Loading order…</div>
      </div>
    );
  }
  if (!order) return null;

  const subtotal = Number(order.subtotal_usd || 0);
  const discount = Number(order.discount_usd || 0);
  const shippingFee = subtotal >= 199 ? 0 : 9.99;
  const total = (subtotal - discount + shippingFee).toFixed(2);

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-10" data-testid="payment-page">
      <div className="eyebrow">Payment</div>
      <h1 className="h-display text-4xl md:text-5xl mt-2">Settle order <span className="text-[var(--terra)]">{order.order_number}</span></h1>
      <p className="mt-3 text-sm text-[var(--ink-soft)] max-w-xl">
        Select your payment rail. Crypto payments confirm in minutes; {cardEnabled ? "cards are processed by VERIFIED Crypto Checkout and settle in USDC on-chain." : "card processing is being onboarded."}
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2" role="tablist">
            <TabBtn active={tab === "card"} onClick={() => setTab("card")} icon={CreditCard} label="Card" testid="tab-card" />
            {CRYPTO_TABS.map((c) => (
              <TabBtn
                key={c.key}
                active={tab === c.key}
                onClick={() => setTab(c.key)}
                icon={Bitcoin}
                label={c.label}
                sub={c.chain}
                testid={`tab-${c.key}`}
              />
            ))}
          </div>

          <div className="mt-6 border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-6 md:p-8">
            {tab === "card" ? (
              <CardPanel
                order={order}
                enabled={cardEnabled}
                cardParam={cardParam}
                total={total}
                onUpdated={(updated) => setOrder(updated)}
              />
            ) : (
              <CryptoPanel
                order={order}
                tabMeta={CRYPTO_TABS.find((c) => c.key === tab)}
                onSubmitted={(updated) => setOrder(updated)}
              />
            )}
          </div>
        </div>

        {/* Order summary */}
        <aside className="lg:col-span-4">
          <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-6 sticky top-6">
            <div className="eyebrow">Order summary</div>
            <div className="mt-3 divide-y divide-[var(--line)]">
              {(order.items || []).map((i) => (
                <div key={`${i.sku}-${i.variant}`} className="flex items-start justify-between py-3 gap-3" data-testid={`pay-item-${i.sku}`}>
                  <div>
                    <div className="text-sm font-medium">{i.name}</div>
                    <div className="font-mono text-[11px] text-[var(--ink-muted)]">{i.sku} · {i.variant} × {i.quantity}</div>
                  </div>
                  <div className="font-display font-medium text-sm whitespace-nowrap">${(i.unit_price * i.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            {discount > 0 && (
              <Row
                label={`Discount · ${[order.affiliate_code, order.promo_code, order.loyalty_points_used ? "Loyalty" : null].filter(Boolean).join(" + ") || "applied"}`}
                value={`−$${discount.toFixed(2)}`}
                testid="order-discount"
              />
            )}
            <Row label="Shipping" value={shippingFee === 0 ? "Free" : `$${shippingFee.toFixed(2)}`} />
            <Row label="Total USD" value={`$${total}`} bold testid="order-total" />
            <div className="mt-4 pt-4 border-t border-[var(--line)] text-xs text-[var(--ink-muted)] flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[var(--terra)] shrink-0 mt-0.5" />
              <span>Order is fulfilled only after payment is confirmed by the Nebula Aminos compliance team. Stock is reserved for 24 hours.</span>
            </div>
            <Link to="/account/orders" className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.16em] text-[var(--ink-muted)] hover:text-[var(--terra)]" data-testid="back-to-orders">
              ← View my orders
            </Link>
          </div>
          <div className="mt-4">
            <LoyaltyRecap order={order} />
          </div>
        </aside>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, sub, testid }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 rounded-full border text-sm flex items-center gap-2 transition-colors ${
      active
        ? "border-[var(--terra)] bg-[var(--terra)]/15 text-[var(--terra)]"
        : "border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--terra)]/40"
    }`}
  >
    <Icon className="h-4 w-4" />
    <span className="font-medium">{label}</span>
    {sub && <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--ink-muted)]">{sub}</span>}
  </button>
);

const Row = ({ label, value, bold, testid }) => (
  <div className={`flex items-baseline justify-between py-2 ${bold ? "border-t border-[var(--line)] mt-2 pt-3" : ""}`}>
    <div className={`text-sm ${bold ? "font-medium" : "text-[var(--ink-muted)]"}`}>{label}</div>
    <div className={`${bold ? "font-display text-xl" : "text-sm font-mono"}`} data-testid={testid}>{value}</div>
  </div>
);

function CardPanel({ order, enabled, cardParam, total, onUpdated }) {
  const cardStatus = order.card_payment?.status;
  const paid = cardStatus === "paid" || (order.status === "approved" && order.payment_method === "card");
  if (paid) return <CardPaid order={order} />;
  if (cardParam === "success" && order.status === "awaiting_payment" && cardStatus !== "failed") {
    return <CardVerifying order={order} onUpdated={onUpdated} />;
  }
  if (!enabled) return <CardWaitlist email={order.user_email} />;
  if (order.status !== "awaiting_payment") {
    return (
      <div className="text-center py-8 text-sm text-[var(--ink-muted)]" data-testid="card-status-locked">
        This order is in status <strong>{order.status}</strong> and cannot accept a new payment.
      </div>
    );
  }
  return <CardPay order={order} total={total} failed={cardStatus === "failed"} />;
}

function CardPay({ order, total, failed }) {
  const [busy, setBusy] = useState(false);
  const payNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/card/checkout", {
        order_number: order.order_number,
        return_origin: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start card checkout");
      setBusy(false);
    }
  };
  return (
    <div className="text-center max-w-md mx-auto py-6" data-testid="card-pay">
      {failed && (
        <div className="mb-5 px-4 py-3 rounded-md border border-[var(--crimson)]/40 bg-[var(--crimson)]/10 text-sm text-[var(--crimson)]" data-testid="card-failed-banner">
          Your last card attempt didn&apos;t go through. You can retry below or switch to crypto.
        </div>
      )}
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--line)] font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">
        <ShieldCheck className="h-3 w-3 text-[var(--terra)]" /> Secure hosted checkout
      </div>
      <h2 className="font-display text-3xl mt-4">Pay by card.</h2>
      <p className="mt-3 text-sm text-[var(--ink-soft)]">
        You&apos;ll be redirected to VERIFIED Crypto Checkout&apos;s secure hosted page to pay with any major debit or credit card. Available wallets (Apple Pay, Google Pay, etc.) depend on the hosted provider and your region. Settlement is delivered to Nebula Aminos in USDC on Polygon.
      </p>
      <button onClick={payNow} disabled={busy} className="btn-primary mt-6 justify-center" data-testid="card-pay-button">
        {busy ? "Redirecting…" : `Pay $${total} by card`} <ArrowRight className="h-4 w-4" />
      </button>
      <p className="mt-6 text-xs text-[var(--ink-muted)]">
        Payments are processed by VERIFIED Crypto Checkout — card details never touch Nebula Aminos servers.
      </p>
    </div>
  );
}

function CardVerifying({ order, onUpdated }) {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      if (tries > 40) {
        clearInterval(t);
        setTimedOut(true);
        return;
      }
      try {
        const { data } = await api.get(`/orders/by-number/${order.order_number}`);
        if (data.status === "approved" || data.card_payment?.status !== order.card_payment?.status) {
          onUpdated(data);
        }
        if (data.status === "approved" || data.card_payment?.status === "failed") clearInterval(t);
      } catch {
        /* keep polling */
      }
    }, 3000);
    return () => clearInterval(t);
  }, [order.order_number]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="text-center py-10" data-testid="card-verifying">
      <Loader2 className="h-8 w-8 mx-auto animate-spin text-[var(--terra)]" />
      <h2 className="font-display text-3xl mt-4">Confirming your payment…</h2>
      <p className="mt-3 text-sm text-[var(--ink-soft)] max-w-md mx-auto">
        We&apos;re waiting for VERIFIED Crypto Checkout&apos;s confirmation. This usually takes a few seconds — don&apos;t close this page.
      </p>
      {timedOut && (
        <p className="mt-4 text-xs text-[var(--ink-muted)] max-w-md mx-auto" data-testid="card-verify-timeout">
          Still processing. You can safely leave — your order will update in{" "}
          <Link to="/account/orders" className="underline text-[var(--terra)]">My orders</Link> once the payment settles.
        </p>
      )}
    </div>
  );
}

function CardPaid({ order }) {
  return (
    <div className="text-center py-8" data-testid="card-paid">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--terra)]/15 text-[var(--terra)] font-mono text-[10.5px] tracking-[0.18em] uppercase">
        <Check className="h-3 w-3" /> Payment confirmed
      </div>
      <h2 className="font-display text-3xl mt-4">You&apos;re all set.</h2>
      <p className="mt-3 text-sm text-[var(--ink-soft)] max-w-md mx-auto">
        Your card payment was confirmed and your order is approved. A receipt has been emailed to {order.user_email}.
      </p>
      {order.receipt_number && (
        <div className="mt-4 font-mono text-sm text-[var(--ink-muted)]" data-testid="card-receipt-number">
          Receipt · {order.receipt_number}
        </div>
      )}
      <Link to="/account/orders" className="mt-6 inline-flex btn-primary" data-testid="card-goto-orders">
        View order status <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function CardWaitlist({ email }) {
  const [val, setVal] = useState(email || "");
  const [done, setDone] = useState(false);
  return (
    <div className="text-center max-w-md mx-auto py-6" data-testid="card-waitlist">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--line)] font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">
        <Clock className="h-3 w-3" /> Coming Soon
      </div>
      <h2 className="font-display text-3xl mt-4">Card payments rolling out.</h2>
      <p className="mt-3 text-sm text-[var(--ink-soft)]">
        We&apos;re finishing onboarding with our card processor. Drop your email and we&apos;ll ping you the moment Visa, MasterCard, and Amex go live for research orders.
      </p>
      {done ? (
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--terra)]/15 text-[var(--terra)] font-mono text-xs uppercase tracking-[0.16em]" data-testid="waitlist-done">
          <Check className="h-4 w-4" /> You&apos;re on the list
        </div>
      ) : (
        <form
          className="mt-6 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!val || !val.includes("@")) { toast.error("Enter a valid email."); return; }
            setDone(true);
            toast.success("You're on the card waitlist.");
          }}
        >
          <input
            type="email"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="you@lab.org"
            className="flex-1 px-4 py-3 border border-[var(--line)] bg-[var(--paper)] font-mono text-sm rounded-full outline-none focus:border-[var(--terra)]"
            data-testid="waitlist-email"
          />
          <button type="submit" className="btn-primary" data-testid="waitlist-submit">
            Join waitlist <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      )}
      <p className="mt-6 text-xs text-[var(--ink-muted)]">In the meantime, pay instantly in crypto — settle in BTC, USDT, or USDC on Solana.</p>
    </div>
  );
}

function CryptoPanel({ order, tabMeta, onSubmitted }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState("");

  // Refresh every second for countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch quote whenever the tab changes
  useEffect(() => {
    setQuote(null);
    setTxHash("");
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/payments/crypto/quote", {
          order_number: order.order_number,
          currency: tabMeta.key,
        });
        setQuote(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not fetch live rate");
      } finally {
        setLoading(false);
      }
    })();
  }, [tabMeta.key, order.order_number]);

  const expiresMs = useMemo(() => (quote ? new Date(quote.expires_at).getTime() : 0), [quote]);
  const remaining = Math.max(0, expiresMs - now);
  const expired = quote && remaining === 0;

  const mm = String(Math.floor(remaining / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      toast.error("Copy failed — long-press to select.");
    }
  };

  const refreshQuote = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/crypto/quote", {
        order_number: order.order_number,
        currency: tabMeta.key,
      });
      setQuote(data);
      toast.success("Rate refreshed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  const submitTx = async () => {
    if (!txHash || txHash.trim().length < 10) {
      toast.error("Paste the transaction hash from your wallet.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payments/crypto/submit", {
        order_number: order.order_number,
        currency: tabMeta.key,
        tx_hash: txHash.trim(),
        sent_amount: quote?.amount_crypto,
      });
      const refreshed = await api.get(`/orders/by-number/${order.order_number}`);
      onSubmitted(refreshed.data);
      toast.success("Transaction received — Nebula Aminos will confirm shortly.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (order.status === "pending_crypto_verification") {
    return (
      <div className="text-center py-8" data-testid="crypto-pending-verification">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--terra)]/15 text-[var(--terra)] font-mono text-[10.5px] tracking-[0.18em] uppercase">
          <Clock className="h-3 w-3" /> Awaiting on-chain confirmation
        </div>
        <h2 className="font-display text-3xl mt-4">Transaction submitted.</h2>
        <p className="mt-3 text-sm text-[var(--ink-soft)] max-w-md mx-auto">
          We&apos;re verifying your <strong>{order.crypto_payment?.currency?.toUpperCase()}</strong> transaction on chain. Your order will flip to <strong>Approved</strong> within ~15 minutes, and a receipt will be emailed to {order.user_email}.
        </p>
        {order.crypto_payment?.tx_hash && (
          <div className="mt-5 font-mono text-[11px] break-all text-[var(--ink-muted)]" data-testid="submitted-tx-hash">
            tx · {order.crypto_payment.tx_hash}
          </div>
        )}
        <Link to="/account/orders" className="mt-6 inline-flex btn-primary" data-testid="goto-orders">
          View order status <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  if (order.status !== "awaiting_payment") {
    return (
      <div className="text-center py-8 text-sm text-[var(--ink-muted)]">
        This order is in status <strong>{order.status}</strong> and cannot accept a new payment.
      </div>
    );
  }

  if (loading || !quote) {
    return <div className="text-center py-12 font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-muted)]">Fetching live {tabMeta.ticker} rate…</div>;
  }

  return (
    <div data-testid={`crypto-panel-${tabMeta.key}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Send exactly</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-display text-4xl font-semibold tracking-tight" data-testid="crypto-amount">{quote.amount_crypto}</span>
            <span className="font-mono text-sm text-[var(--ink-muted)]">{quote.ticker}</span>
          </div>
          <div className="text-xs text-[var(--ink-muted)] mt-1">≈ ${quote.amount_usd} · 1 {quote.ticker} = ${Number(quote.rate_usd).toLocaleString(undefined, { maximumFractionDigits: 6 })}</div>
        </div>
        <div className={`text-right ${expired ? "text-[var(--crimson)]" : "text-[var(--ink-muted)]"}`}>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em]">Quote {expired ? "expired" : "expires in"}</div>
          <div className="font-mono text-2xl mt-0.5" data-testid="quote-countdown">{expired ? "00:00" : `${mm}:${ss}`}</div>
          {expired && (
            <button onClick={refreshQuote} className="mt-2 text-xs underline text-[var(--terra)]" data-testid="refresh-quote">
              Refresh rate
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
        <div className="bg-white p-4 rounded-md border border-[var(--line)]" data-testid="crypto-qr">
          <QRCodeCanvas value={quote.uri || quote.address} size={180} bgColor="#ffffff" fgColor="#000000" />
        </div>
        <div>
          <div className="eyebrow">Send to · {quote.chain}</div>
          <div className="mt-2 flex items-center gap-2 p-3 bg-[var(--paper)] border border-[var(--line)] rounded-md">
            <code className="font-mono text-xs break-all flex-1" data-testid="crypto-address">{quote.address}</code>
            <button
              onClick={() => copy(quote.address, "addr")}
              className="px-2 py-1.5 border border-[var(--line)] rounded-md hover:border-[var(--terra)]"
              data-testid="copy-address"
              aria-label="Copy address"
            >
              {copied === "addr" ? <Check className="h-4 w-4 text-[var(--terra)]" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 p-3 bg-[var(--paper)] border border-[var(--line)] rounded-md">
            <code className="font-mono text-sm flex-1" data-testid="crypto-amount-copy">{quote.amount_crypto} {quote.ticker}</code>
            <button
              onClick={() => copy(String(quote.amount_crypto), "amount")}
              className="px-2 py-1.5 border border-[var(--line)] rounded-md hover:border-[var(--terra)]"
              data-testid="copy-amount"
              aria-label="Copy amount"
            >
              {copied === "amount" ? <Check className="h-4 w-4 text-[var(--terra)]" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <ul className="mt-4 space-y-1.5 text-xs text-[var(--ink-muted)]">
            <li>· Send <strong className="text-[var(--ink)]">exactly</strong> {quote.amount_crypto} {quote.ticker} on the <strong className="text-[var(--ink)]">{quote.chain}</strong> network.</li>
            <li>· Sending the wrong asset or wrong chain will result in lost funds.</li>
            <li>· After broadcasting, paste your transaction hash below.</li>
          </ul>
        </div>
      </div>

      <div className="mt-8 border-t border-[var(--line)] pt-6">
        <div className="eyebrow">2 · Confirm your transaction</div>
        <p className="text-sm text-[var(--ink-soft)] mt-1">Paste the tx hash from your wallet or block explorer.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            placeholder={`${tabMeta.ticker} transaction hash`}
            className="flex-1 px-4 py-3 border border-[var(--line)] bg-[var(--paper)] font-mono text-xs rounded-md outline-none focus:border-[var(--terra)]"
            data-testid="tx-hash-input"
          />
          <button onClick={submitTx} disabled={submitting} className="btn-primary justify-center" data-testid="submit-tx">
            {submitting ? "Submitting…" : "Submit transaction"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {txHash && (
          <a
            href={`${tabMeta.explorer}${txHash.trim()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--terra)] hover:text-[var(--terra-2)]"
            data-testid="view-on-explorer"
          >
            <ExternalLink className="h-3 w-3" /> View on block explorer
          </a>
        )}
      </div>
    </div>
  );
}
